import type { VehicleCepikDataDto } from '../types/models.types';
import type { RaportCepikProps } from '@/components/Raport/RaportCepik/RaportCepik.types';
import { formatDateToDDMMYYYY } from './car-basic-info.mapper';

export type MappedCepikSection = RaportCepikProps | null;

type NullableVehicleCepikDataDto = {
  [Key in keyof VehicleCepikDataDto]: VehicleCepikDataDto[Key] | null;
};

const normalizeText = (value: string | null | undefined): string | null => {
  const normalizedValue = value?.trim();
  return normalizedValue || null;
};

const formatNumber = (value: number | null | undefined): string | null => {
  if (value === undefined || value === null) return null;
  return String(value);
};

const formatNumberWithUnit = (
  value: number | null | undefined,
  unit: string,
): string | null => {
  if (value === undefined || value === null) return null;
  return `${value} ${unit}`;
};

const formatDate = (value: string | null | undefined): string | null => {
  const normalizedValue = normalizeText(value);
  return normalizedValue ? formatDateToDDMMYYYY(normalizedValue) : null;
};

const formatInsuranceStatus = (
  isValid: boolean | null | undefined,
): string | null => {
  if (isValid === undefined || isValid === null) return null;
  return isValid ? 'Aktualna' : 'Nieaktualna';
};

const formatBoolean = (
  value: boolean | null | undefined,
): string | null => {
  if (value === undefined || value === null) return null;
  return value ? 'Tak' : 'Nie';
};

const hasMappedValues = (section: Record<string, string | null>): boolean => {
  return Object.values(section).some((value) => value !== null);
};

export const mapCepikSectionToUI = (
  cepikSection: NullableVehicleCepikDataDto | null | undefined,
): MappedCepikSection => {
  if (!cepikSection) return null;

  const mappedSection: RaportCepikProps = {
    technicalData: {
      yearOfProduction: formatNumber(cepikSection.yearOfProduction),
      engineCapacity: formatNumber(cepikSection.engineCapacity),
      enginePower: formatNumber(cepikSection.enginePower),
      fuel: normalizeText(cepikSection.fuelType),
      alternativeFuel: normalizeText(cepikSection.alternativeFuel),
      co2Emission: formatNumber(cepikSection.co2Emission),
      euroNorm: normalizeText(cepikSection.euroNorm),
      averageFuelConsumption: formatNumber(cepikSection.avgFuelConsumption),
      seatsCount: formatNumber(cepikSection.totalSeatCount),
      passengerSeatsCount: formatNumber(cepikSection.seatCount),
      vehicleWeight: formatNumberWithUnit(cepikSection.curbWeight, 'kg'),
      maxTrailerWeightBraked: formatNumberWithUnit(
        cepikSection.maxBrakedTrailerWeight,
        'kg',
      ),
      maxTrailerWeightUnbraked: formatNumberWithUnit(
        cepikSection.maxUnbrakedTrailerWeight,
        'kg',
      ),
      permissibleTotalWeight: formatNumberWithUnit(
        cepikSection.grossVehicleWeight,
        'kg',
      ),
      axlesCount: formatNumber(cepikSection.axleCount),
      wheelbase: formatNumberWithUnit(cepikSection.wheelbase, 'mm'),
      wheelTrack: formatNumberWithUnit(cepikSection.avgTrackWidth, 'mm'),
    },
    documents: {
      documentIssueDate: formatDate(cepikSection.documentIssueDate),
      documentType: normalizeText(cepikSection.vehicleDocumentType),
      documentStatus: normalizeText(cepikSection.vehicleDocumentCondition),
      firstRegistrationDate: formatDate(cepikSection.firstRegistrationDate),
      vin: normalizeText(cepikSection.vin),
    },
    homologation: {
      homologationNumber: normalizeText(
        cepikSection.homologationCertificateNumber,
      ),
      category: normalizeText(cepikSection.vehicleCategory),
      version: normalizeText(cepikSection.homologationVersion),
      variant: normalizeText(cepikSection.homologationVariant),
      type: normalizeText(cepikSection.homologationType),
    },
    eventsSummary: {
      ownersCount: formatNumber(cepikSection.ownerCount),
    },
    currentStatus: {
      registrationVoivodeship: normalizeText(cepikSection.registrationProvince),
      ocInsurance: formatInsuranceStatus(cepikSection.ocInsuranceValid),
      ocInsuranceExpiryDate: formatDate(cepikSection.insuranceExpiryDate),
      technicalInspection: normalizeText(
        cepikSection.technicalInspectionStatus,
      ),
      lastOdometerReading: formatNumberWithUnit(
        cepikSection.odometerValue,
        'km',
      ),
      odometerRolledBack: formatBoolean(cepikSection.odometerRolledBack),
      odometerError: formatBoolean(cepikSection.odometerError),
      ownersCount: formatNumber(cepikSection.ownerCount),
      coOwnersCount: formatNumber(cepikSection.coOwnerCount),
    },
  };

  const mappedGroups = [
    mappedSection.technicalData,
    mappedSection.documents,
    mappedSection.homologation,
    mappedSection.eventsSummary,
    mappedSection.currentStatus,
  ];

  return mappedGroups.some(hasMappedValues) ? mappedSection : null;
};
